import { saveAs } from "file-saver";
import { UNDERSCORE_DATE_FORMAT } from "../paperwork/constants";
import promisePoller, { CANCEL_TOKEN } from "promise-poller";
import moment from "moment";
import { captureException } from "@sentry/browser";
import _ from "lodash";
import { downloadFile } from "../common/common.utils";

export class GuessGenderService {
    /* @ngInject */
    constructor($http) {
        this.$http = $http;
        this.endpoint = process.env.API_DOMAIN + "/api/";
    }

    guess(first_name) {
        let svc = this;
        return svc.$http.get(svc.endpoint + "guess_gender", {
            params: { first_name: first_name },
        });
    }
}

export class GuessLanguageService {
    /* @ngInject */
    constructor($http) {
        this.$http = $http;
        this.endpoint = process.env.API_DOMAIN + "/api/";
    }

    guess(first_name, last_name) {
        let svc = this;
        return svc.$http.get(svc.endpoint + "guess_language", {
            params: { first_name: first_name, last_name: last_name },
        });
    }
}

export class SvixDashboardService {
    /* @ngInject */
    constructor($http) {
        this.$http = $http;
        this.endpoint = process.env.API_DOMAIN + "/api/";
    }

    getUrl(growerSid) {
        let svc = this;
        return svc.$http
            .get(svc.endpoint + "svix_dashboard", {
                params: { grower_organization: growerSid },
            })
            .then(function (response) {
                return response.data.url;
            });
    }
}

export class StripePortalService {
    /* @ngInject */
    constructor($http) {
        this.$http = $http;
        this.endpoint = process.env.API_DOMAIN + "/api/";
    }

    getUrl(growerSid) {
        let svc = this;
        return svc.$http
            .get(svc.endpoint + "stripe_portal", {
                params: { grower_organization: growerSid },
            })
            .then(function (response) {
                return response.data.url;
            });
    }
}

export class PaperworkSignatureService {
    /* @ngInject */
    constructor($http) {
        this.$http = $http;
        this.endpoint = process.env.API_DOMAIN + "/api/";
    }

    get(signerSid, growerSid) {
        let svc = this;
        return svc.$http
            .get(svc.endpoint + "paperworksignatures", {
                params: { requester: growerSid, signer: signerSid },
            })
            .then(function (response) {
                return response.data.signature;
            });
    }
}

export class ReadabilityService {
    /* @ngInject */
    constructor($http) {
        this.$http = $http;
        this.endpoint = process.env.API_DOMAIN + "/api/";
    }
    check(message_body, language) {
        let svc = this;
        return svc.$http.get(svc.endpoint + "readability", {
            params: { text: message_body, language: language },
        });
    }
}

export class LinkService {
    /* @ngInject */
    constructor($http) {
        this.$http = $http;
        this.endpoint = process.env.API_DOMAIN + "/api/";
    }

    create(path, auth_user_sid) {
        let svc = this;
        return svc.$http.post(svc.endpoint + "links", {
            path,
            auth_user: auth_user_sid,
        });
    }
}

export class ReplySuggestionService {
    /* @ngInject */
    constructor($http) {
        this.$http = $http;
        this.endpoint = process.env.API_DOMAIN + "/api/";
    }

    get(message_history, language) {
        let svc = this;
        return svc.$http.post(svc.endpoint + "reply-suggestion", {
            message_history: message_history,
            target_language: language,
        });
    }
}

export class ExportEmployeeDataService {
    /* @ngInject */
    constructor(Restangular, $http, ToastService, PollDownloadService) {
        this.resource = "export";
        this.Restangular = Restangular;
        this.$http = $http;
        this.ToastService = ToastService;
        this.PollDownloadService = PollDownloadService;
    }

    companyExport(options) {
        let svc = this;
        return svc.Restangular.all(svc.resource).customPOST(
            options,
            "company_employees",
        );
    }

    companyExportAndSave(format, subjects, asker) {
        let svc = this;
        let params = {
            export_format: format,
            subjects: subjects,
            asker: asker,
        };
        return this.Restangular.all(this.resource)
            .customPOST(params, "company_employees")
            .then(function (response) {
                return svc.PollDownloadService.get(response).then(
                    function (url) {
                        downloadFile(url);
                    },
                    function (reasons) {
                        svc.ToastService.create(
                            "ExportEmployeeDataService.POLL_FAILED",
                        );
                        captureException("Onboarding data export failed", {
                            contexts: {
                                error: {
                                    attempts: reasons.length,
                                    last_attempt: _.last(reasons),
                                },
                            },
                        });
                    },
                );
            });
    }

    exportAndSave(format, subjectSids, asker, farm) {
        let svc = this;
        let params = {
            export_format: format,
            subjects: subjectSids,
            asker: asker,
            farm: farm,
        };
        return this.Restangular.all(this.resource)
            .customPOST(params, "employees")
            .then(function () {
                svc.ToastService.create("exportNotification.REQUESTED");
            });
    }
}

export class WAFLAContractService {
    /* @ngInject */
    constructor($http) {
        this.$http = $http;
        this.resource = "waflacontracts";
    }

    retrieve(grower_airtable_id) {
        let svc = this;
        return svc.$http
            .get(
                _.join(
                    [
                        process.env.API_DOMAIN,
                        "api",
                        svc.resource,
                        grower_airtable_id,
                    ],
                    "/",
                ),
                { skipAuthorization: true },
            )
            .then((response) => response.data);
    }
}

export class PaperworkQueryService {
    /* @ngInject */
    constructor($http) {
        this.$http = $http;
        this.endpoint = process.env.API_DOMAIN + "/api/";
    }

    query(model, params, grower_sid) {
        let svc = this;
        return svc.$http
            .post(svc.endpoint + "paperworkquery", {
                model,
                params,
                grower_organization: grower_sid,
            })
            .then(function (response) {
                return response.data;
            });
    }
}

export class EmployeeInformationQueryService {
    /* @ngInject */
    constructor($http) {
        this.$http = $http;
        this.endpoint = process.env.API_DOMAIN + "/api/";
    }

    query(model, params, columns, grower_sid) {
        let svc = this;
        return svc.$http
            .post(svc.endpoint + "employeeinformationquery", {
                model,
                params,
                grower_organization: grower_sid,
                columns,
            })
            .then(function (response) {
                return response.data;
            });
    }

    export(model, params, columns, grower_sid) {
        let svc = this;
        return svc.$http
            .post(svc.endpoint + "employeeinformationquery/export", {
                model,
                params,
                grower_organization: grower_sid,
                columns,
            })
            .then(function (response) {
                return response.data;
            });
    }
}

export class FARMExportService {
    /* @ngInject */
    constructor(Restangular) {
        this.Restangular = Restangular;
        this.resource = "farmexports";
    }

    create(data) {
        // Expect data to include start_date, end_date, grower_organization
        // Returns a promise resolving to a response containing poll_url & download_url
        return this.Restangular.all(this.resource).customPOST(data);
    }
}

export class WorkerProtectionStandardAuditExportService {
    /* @ngInject */
    constructor(Restangular) {
        this.Restangular = Restangular;
        this.resource = "workerprotectionstandardauditexports";
    }

    create(data) {
        return this.Restangular.all(this.resource).customPOST(data);
    }
}

export class PollDownloadService {
    /* @ngInject */
    constructor($q) {
        this.$q = $q;
    }

    get(response) {
        let svc = this;
        return svc.$q.when(
            promisePoller({
                retries: 300,
                interval: 1000,
                taskFn: function () {
                    return svc.$q
                        .when(
                            fetch(response.poll_url, {
                                method: "HEAD",
                                redirect: "manual",
                            }),
                        )
                        .then(
                            function (pollResponse) {
                                let deferred = svc.$q.defer();
                                let status = pollResponse.status;
                                if (status > 400) {
                                    deferred.reject(pollResponse);
                                } else if (
                                    status === 0 &&
                                    pollResponse.type === "opaqueredirect"
                                ) {
                                    deferred.resolve(response.download_url);
                                } else {
                                    deferred.resolve(response.download_url);
                                }
                                return deferred.promise;
                            },
                            function (error) {
                                let deferred = svc.$q.defer();
                                deferred.reject(error);
                                return deferred.promise;
                            },
                        );
                },
            }),
        );
    }

    getManifest(response, options) {
        let svc = this;

        return svc.$q.when(
            promisePoller({
                retries: 120,
                interval: 500,
                shouldContinue: function (error) {
                    return Boolean(error) && !error.terminal;
                },
                taskFn: function () {
                    if (options.isCancelled()) {
                        return svc.$q.reject(CANCEL_TOKEN);
                    }

                    return svc.$q
                        .when(
                            fetch(response.manifest_poll_url, {
                                method: "HEAD",
                                redirect: "manual",
                                credentials: "omit",
                            }),
                        )
                        .then(function (pollResponse) {
                            let isOpaqueRedirect =
                                pollResponse.status === 0 &&
                                pollResponse.type === "opaqueredirect";
                            if (!pollResponse.ok && !isOpaqueRedirect) {
                                return svc.$q.reject({
                                    status: pollResponse.status,
                                });
                            }

                            return svc.$q.when(
                                fetch(response.manifest_url, {
                                    credentials: "omit",
                                }),
                            );
                        })
                        .then(function (manifestResponse) {
                            if (!manifestResponse.ok) {
                                return svc.$q.reject({
                                    status: manifestResponse.status,
                                });
                            }
                            return svc.$q.when(manifestResponse.json());
                        })
                        .then(function (manifest) {
                            if (manifest.status === "failed") {
                                return svc.$q.reject({
                                    terminal: true,
                                    previewFailed: true,
                                });
                            }
                            if (!isValidPreviewManifest(manifest)) {
                                return svc.$q.reject({
                                    terminal: true,
                                    invalidManifest: true,
                                });
                            }

                            return manifest;
                        });
                },
            }),
        );
    }
}

function isValidPreviewManifest(manifest) {
    if (
        manifest.status !== "ready" ||
        !Number.isInteger(manifest.page_count) ||
        manifest.page_count <= 0 ||
        !Array.isArray(manifest.pages) ||
        manifest.pages.length !== manifest.page_count
    ) {
        return false;
    }

    manifest.pages.sort(function (firstPage, secondPage) {
        return firstPage.number - secondPage.number;
    });

    return manifest.pages.every(function (page, index) {
        return (
            page.number === index + 1 && page.url && page.width && page.height
        );
    });
}
